import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

import { useApi } from "../api/ApiProvider";
import { errorMessage, queryKeys } from "../api/queries";
import type { FileFormat, UploadPart } from "../api/types";
import { uploadParts } from "../lib/upload";

/**
 * A file on its way, as the screens describe it.
 *
 * Every number here is one the browser watched happen: `bytes_sent` counts the
 * parts secure storage accepted, and nothing is estimated. `state` is what the
 * *browser* is doing — the version's own state is the server's to publish, and
 * the dataset page reads it from the record rather than from this.
 */
export interface FileTransfer {
  /** `datasetId/versionNumber`; one file is one version. */
  key: string;
  dataset_id: string;
  /** What to call it on screen, e.g. `Acme · churn 2026`. */
  dataset_label: string;
  version_number: number;
  filename: string;
  size_bytes: number;
  bytes_sent: number;
  state: "sending" | "sealing" | "sent" | "failed";
  /** The sentence to show when it failed; null otherwise. */
  error: string | null;
}

/** What the wizard hands over when the console has opened a version. */
export interface TransferRequest {
  datasetId: string;
  datasetLabel: string;
  versionNumber: number;
  file: File;
  /** Declared at the version, and re-declared when URLs are re-signed. */
  format: FileFormat;
  uploadId: string;
  parts: UploadPart[];
}

export interface UploadControl {
  /** Every file this browser is sending or has just finished sending. */
  transfers: FileTransfer[];
  /** Start sending; it keeps going while the person moves around the console. */
  start: (request: TransferRequest) => void;
  /** Stop sending. Nothing already accepted is removed — the console explains it. */
  cancel: (key: string) => void;
  /** Forget a finished or failed line once somebody has read it. */
  dismiss: (key: string) => void;
}

const UploadContext = createContext<UploadControl | null>(null);

interface UploadProviderProps {
  children: ReactNode;
}

/**
 * Keep an upload running while the person answers the rest of the questions.
 *
 * The transfer lives here, above the router, because that is what makes "the
 * upload runs behind you" true rather than decorative: leaving the wizard for
 * the dataset page unmounts the form, and a transfer owned by the form would
 * die with it. On the last screen of a 4 GB upload, that is forty minutes of
 * somebody's afternoon.
 *
 * Args:
 *     children: Application subtree.
 *
 * Returns:
 *     A provider wrapping the subtree.
 */
export function UploadProvider({ children }: UploadProviderProps): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const [transfers, setTransfers] = useState<FileTransfer[]>([]);
  const controllers = useRef(new Map<string, AbortController>());

  useEffect(() => {
    const running = controllers.current;
    return () => {
      // The tab is going away; the fetches would be torn down anyway, and
      // aborting them says so rather than leaving them to fail on their own.
      for (const controller of running.values()) {
        controller.abort();
      }
      running.clear();
    };
  }, []);

  const patch = useCallback(
    (key: string, change: Partial<FileTransfer>): void => {
      setTransfers((current) =>
        current.map((transfer) =>
          transfer.key === key ? { ...transfer, ...change } : transfer,
        ),
      );
    },
    [],
  );

  const start = useCallback(
    (request: TransferRequest): void => {
      const key = `${request.datasetId}/${request.versionNumber}`;
      const controller = new AbortController();
      controllers.current.set(key, controller);

      setTransfers((current) => [
        ...current.filter((transfer) => transfer.key !== key),
        {
          key,
          dataset_id: request.datasetId,
          dataset_label: request.datasetLabel,
          version_number: request.versionNumber,
          filename: request.file.name,
          size_bytes: request.file.size,
          bytes_sent: 0,
          state: "sending",
          error: null,
        },
      ]);

      void (async (): Promise<void> => {
        try {
          const parts = await uploadParts(request.file, request.parts, {
            signal: controller.signal,
            onProgress: (progress) =>
              patch(key, { bytes_sent: progress.bytes_sent }),
            resign: async () => {
              const reopened = await api.createVersion(request.datasetId, {
                filename: request.file.name,
                format: request.format,
                size_bytes: request.file.size,
              });
              // Asking again is idempotent only while THIS version is still
              // waiting for its bytes. A different number, or a different
              // upload, means the console opened a NEW version — its object is
              // empty, and the parts already accepted belong to the old one.
              // Sealing the new one with the rest of the file would store a
              // file missing its beginning, and nothing downstream would catch
              // it: the console measures what arrived, never what was
              // declared. So this stops, and says what to do instead.
              if (
                reopened.number !== request.versionNumber ||
                !reopened.upload ||
                reopened.upload.upload_id !== request.uploadId
              ) {
                throw new Error(
                  `Version ${request.versionNumber} stopped waiting for its ` +
                    "bytes while this file was going up, so the console cannot " +
                    "finish it. Upload the file again from the dataset page — " +
                    "nothing you have already answered is lost.",
                );
              }
              return reopened.upload.parts;
            },
          });
          patch(key, { state: "sealing" });
          await api.completeVersion(request.datasetId, request.versionNumber, {
            upload_id: request.uploadId,
            parts,
          });
          patch(key, { state: "sent" });
        } catch (error) {
          if (controller.signal.aborted) {
            // Cancelling already removed the line; there is nothing to say.
            return;
          }
          patch(key, { state: "failed", error: errorMessage(error) });
        } finally {
          controllers.current.delete(key);
          void queryClient.invalidateQueries({
            queryKey: queryKeys.dataset(request.datasetId),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.datasetVersions(request.datasetId),
          });
        }
      })();
    },
    [api, patch, queryClient],
  );

  const cancel = useCallback((key: string): void => {
    controllers.current.get(key)?.abort();
    controllers.current.delete(key);
    setTransfers((current) => current.filter((transfer) => transfer.key !== key));
  }, []);

  const dismiss = useCallback((key: string): void => {
    setTransfers((current) => current.filter((transfer) => transfer.key !== key));
  }, []);

  const value = useMemo<UploadControl>(
    () => ({ transfers, start, cancel, dismiss }),
    [transfers, start, cancel, dismiss],
  );

  return (
    <UploadContext.Provider value={value}>{children}</UploadContext.Provider>
  );
}

/**
 * Access the files this browser is sending.
 *
 * Returns:
 *     The transfers and the three things that can be done to them.
 *
 * Raises:
 *     Error: When called outside of an `UploadProvider`.
 */
export function useUploads(): UploadControl {
  const value = useContext(UploadContext);
  if (!value) {
    throw new Error("useUploads must be used inside an UploadProvider");
  }
  return value;
}
